import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Eye, Lock, Database, Users, FileText } from 'lucide-react'
import { motion } from 'framer-motion'

export default function Privacy() {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  return (
    <motion.div 
      className="min-h-screen bg-gray-900"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-4xl px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div className="mb-12 text-center" variants={fadeInUp}>
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 mr-3 text-orange-400" />
            <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-xl text-gray-300">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="mt-4 text-gray-400">
            Your privacy is important to us. This policy explains how PexiBlock collects, uses, and protects your information.
          </p>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div 
          className="p-6 mb-8 border bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50"
          variants={fadeInUp}
        >
          <h2 className="flex items-center mb-4 text-lg font-semibold text-white">
            <FileText className="w-5 h-5 mr-2 text-orange-400" />
            Quick Navigation
          </h2>
          <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
            {[
              'Information We Collect',
              'How We Use Information',
              'Information Sharing',
              'Data Security',
              'Your Rights',
              'Cookies & Tracking',
              'Data Retention',
              'International Transfers',
              'Contact Us'
            ].map((item, index) => (
              <a 
                key={index}
                href={`#section-${index + 1}`}
                className="text-orange-400 transition-colors hover:text-orange-300"
              >
                {item}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <div className="space-y-8 text-gray-300">
          
          {/* Section 1: Information We Collect */}
          <motion.section id="section-1" variants={fadeInUp}>
            <div className="flex items-center mb-4">
              <Database className="w-6 h-6 mr-3 text-orange-400" />
              <h2 className="text-2xl font-semibold text-white">1. Information We Collect</h2>
            </div>
            <div className="space-y-4">
              <div className="p-4 border-l-4 border-orange-500 rounded-lg bg-slate-800/30">
                <h3 className="mb-2 font-semibold text-white">Personal Information</h3>
                <p>
                  When you use PexiBlock's services, we collect information you provide directly, including:
                </p>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li>Name, email address, and contact information</li>
                  <li>Business information and tax identification numbers</li>
                  <li>Identity verification documents (for KYC compliance)</li>
                  <li>Cryptocurrency wallet addresses</li>
                  <li>Banking and financial account information</li>
                </ul>
              </div>

              <div className="p-4 border-l-4 border-blue-500 rounded-lg bg-slate-800/30">
                <h3 className="mb-2 font-semibold text-white">Transaction Information</h3>
                <p>
                  We collect information about your payment processing activities:
                </p>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li>Payment transaction details and amounts</li>
                  <li>Cryptocurrency conversion records</li>
                  <li>Settlement and payout information</li>
                  <li>Customer payment data (tokenized and encrypted)</li>
                </ul>
              </div>

              <div className="p-4 border-l-4 border-green-500 rounded-lg bg-slate-800/30">
                <h3 className="mb-2 font-semibold text-white">Technical Information</h3>
                <p>
                  We automatically collect technical information when you use our services:
                </p>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li>IP addresses, browser types, and device information</li>
                  <li>API usage logs and integration data</li>
                  <li>Website analytics and usage patterns</li>
                  <li>Security logs and fraud prevention data</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Section 2: How We Use Information */}
          <motion.section id="section-2" variants={fadeInUp}>
            <div className="flex items-center mb-4">
              <Eye className="w-6 h-6 mr-3 text-orange-400" />
              <h2 className="text-2xl font-semibold text-white">2. How We Use Your Information</h2>
            </div>
            <div className="space-y-4">
              <p>We use your information for the following purposes:</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Service Provision</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Processing cryptocurrency payments</li>
                    <li>• Managing your account and dashboard</li>
                    <li>• Providing customer support</li>
                    <li>• Sending transaction confirmations</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Compliance & Security</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Identity verification (KYC/AML)</li>
                    <li>• Fraud prevention and detection</li>
                    <li>• Regulatory compliance reporting</li>
                    <li>• Risk assessment and monitoring</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Business Operations</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Improving our services and features</li>
                    <li>• Analytics and performance monitoring</li>
                    <li>• Marketing and communications</li>
                    <li>• Legal obligations and dispute resolution</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Communication</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Service updates and notifications</li>
                    <li>• Security alerts and warnings</li>
                    <li>• Educational content and newsletters</li>
                    <li>• Policy changes and announcements</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: Information Sharing */}
          <motion.section id="section-3" variants={fadeInUp}>
            <div className="flex items-center mb-4">
              <Users className="w-6 h-6 mr-3 text-orange-400" />
              <h2 className="text-2xl font-semibold text-white">3. Information Sharing and Disclosure</h2>
            </div>
            <div className="space-y-4">
              <p>We may share your information in the following circumstances:</p>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-red-900/20 border-red-500/30">
                  <h3 className="mb-2 font-semibold text-white">We DO NOT sell your personal information</h3>
                  <p className="text-sm">
                    PexiBlock never sells, rents, or trades your personal information to third parties for marketing purposes.
                  </p>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <h3 className="mb-2 font-semibold text-white">Service Providers</h3>
                    <p className="text-sm">
                      We share information with trusted partners who help us operate our business, including payment processors, 
                      cloud hosting providers, and compliance service providers.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <h3 className="mb-2 font-semibold text-white">Legal Requirements</h3>
                    <p className="text-sm">
                      We may disclose information when required by law, court orders, regulatory requests, or to protect 
                      the rights and safety of PexiBlock and our users.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <h3 className="mb-2 font-semibold text-white">Business Transfers</h3>
                    <p className="text-sm">
                      In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                      to the new entity, subject to the same privacy protections.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <h3 className="mb-2 font-semibold text-white">With Your Consent</h3>
                    <p className="text-sm">
                      We may share information for other purposes with your explicit consent or at your direction, 
                      such as integrating with third-party applications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 4: Data Security */}
          <motion.section id="section-4" variants={fadeInUp}>
            <div className="flex items-center mb-4">
              <Lock className="w-6 h-6 mr-3 text-orange-400" />
              <h2 className="text-2xl font-semibold text-white">4. Data Security</h2>
            </div>
            <div className="space-y-4">
              <p>We implement comprehensive security measures to protect your information:</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 border-t-4 border-orange-500 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Encryption</h3>
                  <p className="text-sm">
                    All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption standards.
                  </p>
                </div>
                <div className="p-4 border-t-4 border-blue-500 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Access Controls</h3>
                  <p className="text-sm">
                    Strict access controls, multi-factor authentication, and regular security audits protect your data.
                  </p>
                </div>
                <div className="p-4 border-t-4 border-green-500 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Compliance</h3>
                  <p className="text-sm">
                    We maintain PCI DSS Level 1 compliance and follow industry-standard security frameworks.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: Your Rights */}
          <motion.section id="section-5" variants={fadeInUp}>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 mr-3 text-orange-400" />
              <h2 className="text-2xl font-semibold text-white">5. Your Privacy Rights</h2>
            </div>
            <div className="space-y-4">
              <p>Depending on your location, you may have the following rights:</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Access & Portability</h3>
                  <p className="text-sm">Request access to your personal information and receive a copy in a portable format.</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Correction</h3>
                  <p className="text-sm">Request correction of inaccurate or incomplete personal information.</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Deletion</h3>
                  <p className="text-sm">Request deletion of your personal information, subject to legal and regulatory requirements.</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="mb-2 font-semibold text-white">Objection</h3>
                  <p className="text-sm">Object to certain processing activities, including direct marketing communications.</p>
                </div>
              </div>
              <div className="p-4 border rounded-lg bg-orange-500/10 border-orange-500/30">
                <p className="text-sm">
                  <strong>To exercise your rights,</strong> please contact us at{' '}
                  <a href="mailto:info@pexipay.com" className="text-orange-400 hover:text-orange-300">
                    info@pexipay.com
                  </a>{' '}
                  with your request and proof of identity.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Section 6: Cookies & Tracking */}
          <motion.section id="section-6" variants={fadeInUp}>
            <h2 className="mb-4 text-2xl font-semibold text-white">6. Cookies and Tracking Technologies</h2>
            <div className="space-y-4">
              <p>We use cookies and similar technologies to enhance your experience:</p>
              <div className="space-y-3">
                <div className="p-3 border-l-4 border-orange-500 rounded bg-slate-800/30">
                  <strong className="text-white">Essential Cookies:</strong> Required for basic website functionality and security
                </div>
                <div className="p-3 border-l-4 border-blue-500 rounded bg-slate-800/30">
                  <strong className="text-white">Analytics Cookies:</strong> Help us understand how you use our website to improve performance
                </div>
                <div className="p-3 border-l-4 border-green-500 rounded bg-slate-800/30">
                  <strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences
                </div>
              </div>
              <p className="text-sm">
                You can control cookies through your browser settings, but disabling essential cookies may affect website functionality.
              </p>
            </div>
          </motion.section>

          {/* Section 7: Data Retention */}
          <motion.section id="section-7" variants={fadeInUp}>
            <h2 className="mb-4 text-2xl font-semibold text-white">7. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide our services and comply with legal obligations. 
              Transaction records are typically retained for 7 years as required by financial regulations. Personal information 
              is deleted when no longer needed, unless retention is required by law.
            </p>
          </motion.section>

          {/* Section 8: International Transfers */}
          <motion.section id="section-8" variants={fadeInUp}>
            <h2 className="mb-4 text-2xl font-semibold text-white">8. International Data Transfers</h2>
            <p>
              Your information may be processed in countries other than your own. We ensure appropriate safeguards are in place, 
              including standard contractual clauses and adequacy decisions, to protect your information during international transfers.
            </p>
          </motion.section>

          {/* Section 9: Contact Us */}
          <motion.section id="section-9" variants={fadeInUp}>
            <h2 className="mb-4 text-2xl font-semibold text-white">9. Contact Information</h2>
            <div className="p-6 border rounded-lg bg-slate-800/50 border-slate-700/50">
              <p className="mb-4">If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
              <div className="space-y-2 text-sm">
                <p><strong className="text-white">Email:</strong> info@pexipay.com</p>
                <p><strong className="text-white">Data Protection Officer:</strong> info@pexipay.com</p>
              </div>
            </div>
          </motion.section>

        </div>

        {/* Footer */}
        <motion.div className="pt-8 mt-12 border-t border-gray-700" variants={fadeInUp}>
          <div className="text-center">
            <p className="text-sm text-gray-400">
              This Privacy Policy is effective as of the date last updated above. We may update this policy periodically 
              to reflect changes in our practices or applicable laws.
            </p>
            <div className="mt-4">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-orange-400 transition-colors hover:text-orange-300"
              >
                ← Return to Home
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}